/**
 * Pure calculation logic for the EV home-charging cost comparison tool
 * (content/pages/ev-charging-cost-comparison.md). Ties into content/pages/ev-charger.md:
 * the home-tariff and public-rapid rates below are the exact figures already
 * published there, not re-derived, so this tool never contradicts that page.
 */

import { SEG_RATE_PENCE, SELF_CONSUMPTION_SHARE } from "./energy-calculator.ts";

/** Pence per kWh on a standard home electricity tariff, matching content/pages/ev-charger.md's own published rate. */
export const HOME_STANDARD_RATE_PENCE = 26;
/** Pence per kWh on a cheap overnight EV tariff (e.g. Octopus Go), matching content/pages/ev-charger.md. */
export const HOME_OVERNIGHT_RATE_PENCE = 7;
/** Pence per kWh on a public rapid charger, matching content/pages/ev-charger.md's published range. Rechecked against Zapmap's July 2026 rapid/ultra-rapid pricing (59-92p across the top 10 networks, 80p weighted average). */
export const PUBLIC_RAPID_RATE_LOW_PENCE = 60;
export const PUBLIC_RAPID_RATE_HIGH_PENCE = 90;

/** UK average pump price, pence per litre, checked against current 2026 fuel-price reporting. */
export const PETROL_PRICE_PENCE_PER_LITRE = 160;
/** A "typical" UK petrol car's real-world fuel economy. Not the ~40-45mpg a new car manages, that overstates the fleet on the road: the average across all UK cars actually driven today is closer to 35-38mpg. */
export const TYPICAL_CAR_MPG = 37;
/** UK (imperial) gallon, litres. */
const LITRES_PER_UK_GALLON = 4.546;

export interface EvPreset {
  id: string;
  label: string;
  /** WLTP-derived efficiency, kWh used per 100 miles (lower is more efficient). */
  kwhPer100Miles: number;
}

export const EV_PRESETS: EvPreset[] = [
  // 28.8 kWh/100mi is chosen so this preset's home-standard-tariff figure below
  // reproduces the "7 to 8p a mile" already quoted on content/pages/ev-charger.md,
  // rather than introducing a second, slightly different "typical" figure.
  { id: "typical", label: "Typical UK EV (average)", kwhPer100Miles: 28.8 },
  // 18.5 kWh/100mi (11.5 kWh/100km) matches the current Model 3 Long Range's WLTP rating;
  // the previous 20 overstated its consumption by about 8%.
  { id: "tesla-model-3", label: "Tesla Model 3 Long Range", kwhPer100Miles: 18.5 },
  { id: "hyundai-ioniq-6", label: "Hyundai Ioniq 6", kwhPer100Miles: 20.8 },
  { id: "renault-5-e-tech", label: "Renault 5 E-Tech", kwhPer100Miles: 20.8 },
  { id: "kia-ev3", label: "Kia EV3", kwhPer100Miles: 22.7 },
  { id: "mg4", label: "MG4 EV", kwhPer100Miles: 25 },
  { id: "nissan-leaf", label: "Nissan Leaf", kwhPer100Miles: 30.8 },
];

export function findEvPreset(id: string): EvPreset | undefined {
  return EV_PRESETS.find((preset) => preset.id === id);
}

export interface EvCostComparison {
  homeStandardPencePerMile: number;
  homeOvernightPencePerMile: number;
  /** The self-consumption share actually used, echoed back so the UI can show what it applied. */
  solarShareUsed: number;
  homeSolarPencePerMile: number;
  publicRapidLowPencePerMile: number;
  publicRapidHighPencePerMile: number;
  petrolPencePerMile: number;
}

function pencePerMile(kwhPer100Miles: number, ratePence: number): number {
  return (kwhPer100Miles / 100) * ratePence;
}

/**
 * @param kwhPer100Miles the car's efficiency.
 * @param solarSelfConsumptionShare share of charging assumed to land on self-used solar
 *   rather than the grid (0-1). Defaults to the site-wide SELF_CONSUMPTION_SHARE, but a
 *   caller with a personalized figure (from a returning visitor's energy cost calculator
 *   result) should pass that instead.
 */
export function computeEvCostComparison(
  kwhPer100Miles: number,
  solarSelfConsumptionShare: number = SELF_CONSUMPTION_SHARE
): EvCostComparison {
  const efficiency = Number.isFinite(kwhPer100Miles) && kwhPer100Miles > 0 ? kwhPer100Miles : 0;
  const share = Math.min(Math.max(Number.isFinite(solarSelfConsumptionShare) ? solarSelfConsumptionShare : 0, 0), 1);

  // Self-used solar isn't free: charging from it instead of exporting gives up the SEG
  // income that kWh would otherwise have earned, so its real cost is the export rate, not zero.
  const blendedSolarRate = share * SEG_RATE_PENCE + (1 - share) * HOME_STANDARD_RATE_PENCE;

  const litresPerMile = LITRES_PER_UK_GALLON / TYPICAL_CAR_MPG;
  const petrolPencePerMile = litresPerMile * PETROL_PRICE_PENCE_PER_LITRE;

  return {
    homeStandardPencePerMile: pencePerMile(efficiency, HOME_STANDARD_RATE_PENCE),
    homeOvernightPencePerMile: pencePerMile(efficiency, HOME_OVERNIGHT_RATE_PENCE),
    solarShareUsed: share,
    homeSolarPencePerMile: pencePerMile(efficiency, blendedSolarRate),
    publicRapidLowPencePerMile: pencePerMile(efficiency, PUBLIC_RAPID_RATE_LOW_PENCE),
    publicRapidHighPencePerMile: pencePerMile(efficiency, PUBLIC_RAPID_RATE_HIGH_PENCE),
    petrolPencePerMile,
  };
}
